import { GetServerSidePropsContext } from 'next';
import { getServerSession } from 'next-auth';
import { signIn } from 'next-auth/react';
import { useEffect } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { nextAuthOptions } from '../helpers/nextAuthOptions';
import { getTLD } from '../helpers/domain';
import { PAGE } from '../helpers/pages';
import { userRepository } from '../repositories/User/UserRepository';
import { isOk } from '../domains/Result';
import { useUserContext } from '../contexts/UserContext';
import { User as UserState } from '../types';
import { User } from '../domains/User';
import { formatAuthorizationHeader } from '../helpers/auth';
import formatCallbackURL from '../utils/formatCallbackURL';
import { accountsApi } from '../api/accountsApi';
import { formatCookie, getDomainFromBaseURL } from '../utils/CookieUtils';
import { COOKIE, MAXIMUM_COOKIE_AGE } from '../helpers/constants';
import MetricService from '../services/MetricService';
import axios from 'axios';
import { logger } from '@dist-marketplace-frontend/services';
import { ENABLE_USER_PROFILE_ENDPOINT } from '../utils';

const {
  publicRuntimeConfig: { BASE_URL },
} = getConfig();

type Props = {
  callbackUrl: string;
  authenticated: boolean;
  user: UserState | null;
  error: boolean;
};

export default function SignInPage(props: Props) {
  const { callbackUrl, authenticated, user, error } = props;
  const { push } = useRouter();
  const { dispatchUserProfile } = useUserContext();

  useEffect(() => {
    if (error) {
      push('/');
    } else if (!authenticated) {
      signIn('keycloak', { callbackUrl });
    } else {
      if (!ENABLE_USER_PROFILE_ENDPOINT && user) {
        const formattedUser = {
          name: user.name,
          verification: {
            phone: user.verification.phone,
            email: user.verification.email,
          },
          registrationDate: user.registrationdate,
        };
        dispatchUserProfile({ type: 'UPDATE', payload: formattedUser });
      }
      /**
       * We use native methods of updating the URL, since those methods associated with Next Router,
       * such as push and replace, may lead to the page failing to load if the page uses a rewrite.
       * This includes pages such as myads on the dashboard, whose URL is rewritten from "listings" to "myads".
       */
      window.location.href = callbackUrl;
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, error]);

  if (error) return <div>Unable to log in</div>;

  if (!authenticated) return <div>Redirecting to login...</div>;

  return <div>Redirecting...</div>;
}

type SetCookieHeader = { 'set-cookie': string[] };

const hasSetCookieHeader = (headers: any): headers is SetCookieHeader => {
  return Boolean(
    headers && headers['set-cookie'] && headers['set-cookie'].length !== 0,
  );
};

const formatDevCookies = (headers: SetCookieHeader) => {
  const header = headers['set-cookie'];
  return header.map((cookie) =>
    cookie
      .replace('.donedealtest.local', 'localhost')
      .replace('donedealtest.accounts', 'localhost'),
  );
};

const formatPreviewCookies = (headers: SetCookieHeader) => {
  const header = headers['set-cookie'];
  return header.map((cookie) =>
    cookie
      .replace('.donedealtest.local', '.dsch.build')
      .replace('donedealtest.accounts', '.dsch.build'),
  );
};

const formatCookies = (headers: SetCookieHeader, tld: string) => {
  const header = headers['set-cookie'];
  return header.map((cookie) =>
    cookie.replace('.com', `${tld}`).replace('.accounts', `${tld}`),
  );
};

const formatUser = (user: User): UserState => ({
  id: parseInt(user.id),
  name: user.name?.replace('+', ' ').split(' ')[0],
  type: Boolean(user.trader) ? 'TRADER' : 'PRIVATE', // doesn't matter here
  verification: {
    email: user.isEmailVerified,
    phone: user.isPhoneVerified,
    allowedAccess: false,
  },
  registrationdate: 'n/a', // doesn't matter here
  termsVersionAccepted: 'n/a', // doesn't matter here
  isUserVerified: user.isEmailVerified || user.isPhoneVerified,
});

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const req = context.req;
  const res = context.res;
  const query = context.query;
  const isDev = process.env.NODE_ENV === 'development';
  const isPreview = process.env.ENVIRONMENT_NAME === 'preview';
  const tld = getTLD(req) as string;
  const baseUrl = `${BASE_URL}${tld}`;
  const session = await getServerSession(req, res, nextAuthOptions);

  const { callbackUrl: unformattedCallbackURL, ...rest } = query;

  /**
   * Next auth only handles on query in the callback URL. In order to retain all the query
   * parameters applied on signin, we must do some formatting with the query object.
   * */
  const callbackUrl = formatCallbackURL({
    unformattedCallbackURL,
    queries: rest,
  });

  if (query.error) {
    logger.error({
      route: PAGE.SIGNIN,
      message: `Received error query parameter, equal to: ${query.error.toString()}`,
    });

    return {
      props: {
        error: true,
      },
    };
  }

  if (session && !session.error && session.accessToken) {
    const cookies: string[] = [];
    try {
      const { headers } = await accountsApi.retrieveLegacySessionFromJWT(
        formatAuthorizationHeader(session.accessToken),
      );
      if (hasSetCookieHeader(headers)) {
        if (isDev) cookies.push(...formatDevCookies(headers));
        else if (isPreview) cookies.push(...formatPreviewCookies(headers));
        else cookies.push(...formatCookies(headers, tld));
      } else {
        logger.error({
          route: PAGE.SIGNIN,
          message: 'Expected set-cookie header, received none.',
        });
      }

      const response = await userRepository.get(
        formatAuthorizationHeader(session.accessToken),
      );
      if (isOk(response)) {
        const userID = response.ok.user.id.toString();
        const sessionUID = formatCookie(COOKIE.DD_CLIENT_UID, userID, {
          domain: getDomainFromBaseURL(baseUrl),
          maxAge: MAXIMUM_COOKIE_AGE,
          httpOnly: false,
        });
        cookies.push(sessionUID);
        res?.setHeader('set-cookie', cookies);
        return {
          props: {
            callbackUrl: callbackUrl || baseUrl,
            authenticated: true,
            user: formatUser(response.ok.user),
          },
        };
      } else {
        logger.error({
          route: PAGE.SIGNIN,
          message: 'Error retrieving user with session',
          error: response.error,
        });
        return {
          props: {
            error: true,
          },
        };
      }
    } catch (error) {
      logger.error({
        route: PAGE.SIGNIN,
        message: 'There was an error getting the legacy session for session',
        error,
      });
      if (axios.isAxiosError(error)) {
        const metricService = MetricService.get();
        metricService.incrementRequestErrorCount('signin', {
          statusCode: error.response?.status,
        });
      }
      return {
        props: {
          error: true,
        },
      };
    }
  }
  /**
   * This is not an error.
   * The user has been redirected here from the authGuard.
   * We pass the callback url to client side signIn so the user continues on their journey
   */
  return {
    props: {
      callbackUrl,
      authenticated: false,
      user: null,
      error: false,
    },
  };
};
